import * as H from "history";
import { flatMap, isEqual, uniq } from "lodash";
import * as React from "react";
import { Link, match as Match } from "react-router-dom";

import { ExportType } from "@app/exports/enumerations";
import { ProjectRunConfigurationResult, RegionFilters } from "@app/projects";
import { NetworkAssessmentAnalysisType, ProjectRunConfigurationType, ProviderNetworkType } from "@app/projects/enumerators";
import { ProjectRouteParams } from "@app/projects/projectRouteParams";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { AccuracyAggregateInspectionResult, AccuracyAggregateInspectionResultGroup, AccuracyBandAggregate, AccuracyInspectionResponse } from "@app/projects/results/accuracy";
import { RegionGranularity } from "@app/region-sets";
import { TooltipWrapper } from "@app/shared";
import Pie from "@app/shared/pie";
import { ProgressBarBand } from "@app/shared/progressBarBand";
import { QESAlert } from "@app/shared/qes-standards";
import { PrimaryTable as SkeletonTable } from "@app/shared/skeleton-components";
import { ColumnConfig, ColumnGroup, SortDirections, TextAlignment, Waffles } from "@app/shared/waffles";
import { AccuracyTemplate, AccuracyTemplateContext } from "@app/templates";
import { AccuracyTemplateInspectionGroup, AccuracyTemplateScoringBand, AccuracyTemplateScoringBandGroup } from "@app/templates/accuracy/models";
import AccuracyService from "./accuracyService";
import AccuracyTooltipLegend from "./AccuracyTooltipLegend";
import {InspectorGroupNames } from "./";
import { JSX } from "react";

const accuracyService = new AccuracyService();

type FlatAggregateRecord = Pick<AccuracyAggregateInspectionResultGroup, "groupName" | "inspectionGroupUid"> & AccuracyAggregateInspectionResult;

type AccuracyAggregateInspectionDetailsProps = CountyProjectContextProps & {
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  compareToRunConfiguration?: ProjectRunConfigurationResult;
  exportEndpoint: string;
  location: H.Location<ProjectRouteParams>;
  match: Match;
  projectId: number;
  regionFilters: RegionFilters;
  runConfiguration: ProjectRunConfigurationResult;
  setDualMembershipMode: (isDualMembershipMode: boolean, projectRunConfigurationType?: ProjectRunConfigurationType, primaryConfigurationId?: number, compareToRunConfigurationId?: number, forceLoading?: boolean) => void;
}

interface AccuracyAggregateInspectionDetailsState {
  aggregatedRecords: FlatAggregateRecord[];
  dataExpansions: Map<string, AccuracyAggregateInspectionWafflesToppings[]>;
  error: boolean;
  isSorting: boolean;
  records: FlatAggregateRecord[];
  selectedRecords: string[];
  selectedSortingColor: string;
  sortedColumn: string;
  sortedRecords: FlatAggregateRecord[];
  sortingColors: string[];
}

class AccuracyAggregateInspectionWafflesToppings {
  public accuracy: React.ReactNode;
  public inspection: React.ReactNode;
  public key: string;

  constructor(inspection: React.ReactNode, accuracy: React.ReactNode, key: string, scoringBands: ScoringBandNode[]) {
    /* eslint-disable sort-keys */
    this.inspection = inspection;
    this.accuracy = accuracy;
    this.key = key;

    for (const scoringBand of scoringBands) {
      Object.assign(this, {
        [`${scoringBand.band}-Percentage`]: scoringBand.percentage,
        [`${scoringBand.band}-Provider-Count`]: scoringBand.providerCount,
      });
    }
    /* eslint-enable sort-keys */
  }
}

class ScoringBandNode {
  public band: string;
  public percentage: string;
  public providerCount: string;

  constructor(band: string, percentage: string, providerCount: string) {
    this.band = band;
    this.percentage = percentage;
    this.providerCount = providerCount;
  }
}

class AccuracyAggregateInspectionDetails extends React.Component<AccuracyAggregateInspectionDetailsProps, AccuracyAggregateInspectionDetailsState> {
  private bandGroups: AccuracyTemplateScoringBandGroup[] = [];
  private inspectionGroups: AccuracyTemplateInspectionGroup[] = [];

  constructor(props: AccuracyAggregateInspectionDetailsProps) {
    super(props);

    this.state = {
      aggregatedRecords: [],
      dataExpansions: new Map<string, AccuracyAggregateInspectionWafflesToppings[]>(),
      error: false,
      isSorting: false,
      records: [],
      selectedRecords: [],
      selectedSortingColor: null,
      sortedColumn: null,
      sortedRecords: [],
      sortingColors: [],
    };
  }

  public async componentDidMount() {
    this.useAggregateResults(this.props.projectId, this.props.networkAssessmentAnalysisType, this.props.regionFilters, this.props.runConfiguration);

    if (this.props.location.state && this.props.location.state.runConfiguration) {
      const config = this.props.location.state.runConfiguration;
      const compareToConfig = this.props.location.state.compareToRunConfiguration;
      await this.props.setDualMembershipMode(this.props.location.state.isDualMembershipMode, config.projectRunConfigurationType, config.projectRunConfigurationId, compareToConfig ? compareToConfig.projectRunConfigurationId : undefined);
    }
  }

  public async componentDidUpdate(prevProps: AccuracyAggregateInspectionDetailsProps, prevState: AccuracyAggregateInspectionDetailsState) {
    if (!isEqual(prevState.records, this.state.records)) {
      this.createDataExpansions();
    }

    if (prevProps.projectId !== this.props.projectId || prevProps.networkAssessmentAnalysisType !== this.props.networkAssessmentAnalysisType || prevProps.regionFilters !== this.props.regionFilters || prevProps.runConfiguration !== this.props.runConfiguration) {
      this.useAggregateResults(this.props.projectId, this.props.networkAssessmentAnalysisType, this.props.regionFilters, this.props.runConfiguration);

      this.setState({
        isSorting: false,
        selectedRecords: [],
        sortedColumn: null,
      });
    }
  }

  public render() {
    const haveResultsToRender = this.state.sortedRecords.length > 0;

    return (
      <AccuracyTemplateContext.Consumer>
        {(accuracyTemplate) => (
          <>
            {!haveResultsToRender &&
              <>
                {this.state.error &&
                  <QESAlert type="danger" persist={true}>
                    <strong>Something went wrong!</strong>
                    Results &quot;by inspection&quot; could not be retrieved.
                  </QESAlert>
                }
                <SkeletonTable />
              </>
            }
            {(haveResultsToRender && accuracyTemplate) && this.buildWaffles(accuracyTemplate)}
          </>
        )}
      </AccuracyTemplateContext.Consumer>
    );
  }

  private createDataExpansions() {
    const dataExpansions = new Map<string, AccuracyAggregateInspectionWafflesToppings[]>();

    const waffleToppings = this.getWaffleToppings(this.state.sortedRecords);

    for (const r of this.state.aggregatedRecords) {
      const getGroupUid = (waffleToppingKey: string) => {
        const record = this.state.records.find((x) => x.inspectionUid === waffleToppingKey);

        if (record) {
          return record.inspectionGroupUid;
        }
      };

      const value = waffleToppings.filter((x) => getGroupUid(x.key.replace(`${r.groupName}-`, "")) === r.inspectionGroupUid);
      dataExpansions.set(this.getPrefixedKey(r), value);
    }

    this.setState({
      dataExpansions,
    });
  }

  private buildWaffles = (accuracyTemplate: AccuracyTemplate) => {
    this.storeInspectionsAndBands(accuracyTemplate);

    let data: AccuracyAggregateInspectionWafflesToppings[] = [];
    let dataExpansions: Map<string, AccuracyAggregateInspectionWafflesToppings[]>;
    if (this.state.isSorting) {
      data = this.getWaffleToppings(this.state.sortedRecords);
      dataExpansions = null;
    } else {
      data = this.getWaffleToppings(this.state.aggregatedRecords);
      dataExpansions = this.state.dataExpansions;
    }

    const dataKeys = data.map((d) => d.key);
    // with how data expansions work, the "parent" rows aren't 
    // actually selectable, but reflect which of their children are selected
    // so we won't count those in the total number of records,
    // which is used to determine the overall bulk checkbox state
    const totalNumberOfRecords = Array.from(dataExpansions || []).reduce((result, [key, value]) => {
      if (dataKeys.includes(key)) {
        result += value.length;
      }
      return result;
    }, 0);

    return (
      <Waffles<AccuracyAggregateInspectionWafflesToppings>
        bulkActionBar={this.getBulkActionBar()}
        columnGroups={this.getColumnGroups(accuracyTemplate)}
        columns={this.getColumnConfig(accuracyTemplate)}
        customSort={this.customSort}
        data={data}
        dataExpansions={dataExpansions}
        dataExpansionsDefaultOpen={true}
        dataExpansionsHaveIndividualCheckboxes={true}
        fullWidth={true}
        handleCheckboxChange={this.setSelectedRecords}
        loading={!this.state.records.length}
        passThroughSorting={true}
        selectedKeys={this.state.selectedRecords}
        showCheckboxes={true}
        testId="accuracy-aggregate-inspection"
        totalNumberOfRecords={totalNumberOfRecords}
      />
    );
  }

  /**
   * Retrieves aggregate results for a given run configuration.
   * If no run configuration is supplied, returns an empty array.
   *
   * Transforms the retrieved records into a flat structure.
   * @param runConfiguration The run configuration.
   * @param networkAssessmentAnalysisType The analysis type.
   */
  private useAggregateResults = (projectId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, regionFilters: RegionFilters, runConfiguration?: ProjectRunConfigurationResult) => {
    if (!runConfiguration) {
      this.setState({
        error: true,
      });
    } else {
      const {
        county: countyRegionCode,
        specialty: specialtyGroupCode,
        state: stateRegionCode,
      } = regionFilters || {} as RegionFilters;

      let regionSetId: number;
      if (!stateRegionCode && !countyRegionCode) {
        regionSetId = runConfiguration.serviceAreaRegionSetId;
      } else if (stateRegionCode && !countyRegionCode) {
        regionSetId = runConfiguration.getState(stateRegionCode).canonicalRegionSetId;
      } else if (countyRegionCode) {
        regionSetId = runConfiguration.getCounty(countyRegionCode).canonicalRegionSetId;
      }

      accuracyService
        .accuracyAggregateInspectionResults(projectId, runConfiguration.projectRunConfigurationId, networkAssessmentAnalysisType, regionSetId, specialtyGroupCode)
        .then((xs) => this.transformAggregates(xs), () => this.setState({ error: true }));
    }
  }

  /**
   * Transforms the aggregates into a flattened array of records for use in the table.
   * @param aggregates The aggregates in question.
   */
  private transformAggregates = (response: AccuracyInspectionResponse) => {
    const aggregates = response?.aggregates;
    const aggregatedRecords: FlatAggregateRecord[] = [];

    for (const group of aggregates) {
      const flatAggregateRecord: FlatAggregateRecord = {
        accuracyIndex: null,
        bandAggregates: group.groupBandAggregates,
        groupName: group.groupName,
        inspectionGroupUid: group.inspectionGroupUid,
        inspectionUid: group.inspectionGroupUid,
        name: group.groupName,
        providerCount: null,
        providerFailPercentage: null,
        providerPassPercentage: null,
      };

      aggregatedRecords.push(flatAggregateRecord);
    }

    const records = flatMap(aggregates, (aggregate) => {
      return aggregate.results.map<FlatAggregateRecord>((result) => ({
        groupName: aggregate.groupName,
        inspectionGroupUid: aggregate.inspectionGroupUid,
        ...result,
      }));
    });

    // stores the possible color options for sorting the accuracy pie chart
    const sortingColors: string[] = [];

    for (const record of records) {
      const bands: ProgressBarBand[] = this.getProgressBarBands(record.bandAggregates);

      for (const band of bands) {
        if (!sortingColors.includes(band.color)) {
          sortingColors.push(band.color);
        }
      }
    }

    // in order to avoid modifying the original records' sort order when a sort occurs, clone them
    this.setState({
      aggregatedRecords,
      records,
      sortedRecords: JSON.parse(JSON.stringify(records)),
      sortingColors,
    });
  }

  private storeInspectionsAndBands = (accuracyTemplate: AccuracyTemplate) => {
    if (!isEqual(this.inspectionGroups, accuracyTemplate.criteria.inspectionGroups)) {
      this.inspectionGroups = accuracyTemplate.criteria.inspectionGroups;
    }

    if (!isEqual(this.bandGroups, accuracyTemplate.scoring.bandGroups)) {
      this.bandGroups = accuracyTemplate.scoring.bandGroups;
    }
  }

  private getBulkActionBar = () => {
    let specialtyGroupCode: string = null;
    let stateRegionCode: string = null;

    if (this.props.regionFilters) {
      specialtyGroupCode = this.props.regionFilters.specialty;
      stateRegionCode = this.props.regionFilters.state;
    }

    let selectedRegionSetIds = this.props.accuracyResult.aggregateDetails && this.props.accuracyResult.aggregateDetails.length > 0
      ? this.props.countyRegionSetIds.filter(c => this.props.accuracyResult.aggregateDetails.map((r) => r.regionCode).includes(c.state)).map(c => c.regionSetId)
      : uniq(this.props.accuracyResult.details.map(d => d.regionSetId));
    // if no records are selected selected, use the current state region set id as the "default" selection for export properties or if there is no state region set id use all county region set ids
    if (selectedRegionSetIds.length === 0) {
      if (stateRegionCode) {
        selectedRegionSetIds = this.props.countyRegionSetIds.filter(c => c.state === stateRegionCode).map(c => c.regionSetId);
      }
      else
        selectedRegionSetIds = this.props.countyRegionSetIds.map(id => id.regionSetId);
    }

    let selectedSpecialtyGroupCodes: string[];
    if (specialtyGroupCode) {
      selectedSpecialtyGroupCodes = [specialtyGroupCode];
    }

    return (
      <span className="bulk-action-group">
        <Link
          to={{
            pathname: this.props.exportEndpoint,
            state: {
              availableExportTypes: [ExportType.AllInspections, ExportType.ActionableProviders, ExportType.StatusReport],
              exportNetworkProviderCount: this.props.accuracyResult.aggregateDetails?.reduce((acc, ad) => acc + ad.providerCount, 0),
              filters: this.props.filters,
              granularity: RegionGranularity.State,
              inspectionUids: this.getSelectedRecords(),
              networkTypes: [ProviderNetworkType.Network],
              projectResourceId: this.props.project.resource.resourceId,
              projectResourceName: this.props.project.resource.name,
              projectURL: this.props.location.pathname + this.props.location.search,
              regionSetIds: selectedRegionSetIds,
              specialtyGroupCodes: selectedSpecialtyGroupCodes
            },
          }}
        >
          <i className="fa fa-cloud-download" /> Create Report
        </Link>
      </span>
    );
  }

  private getSelectedRecords = () => {
    const inspectionUids: string[] = [];

    for (const record of this.state.selectedRecords) {
      // removing the waffles expansion prefix
      const inspectionUid = record.replace("waffles-expansion:", "");
      inspectionUids.push(inspectionUid);
    }

    return inspectionUids;
  }

  private getColumnConfig = (accuracyTemplate: AccuracyTemplate) => {
    const columns: ColumnConfig = {
      accuracy: {
        alignment: TextAlignment.Center,
        displayText: "Accuracy",
        handleSpecialSort: this.setSelectedSortingColor,
        noResize: true,
        specialSort: this.createSpecialSortElement(),
        testId: "accuracy",
      },
      inspection: {
        displayText: "Inspection",
        noResize: true,
        sticky: true,
        testId: "inspection",
      },
    };

    accuracyTemplate.scoring.bandGroups.forEach((bg) => {
      bg.bands.forEach((b) => {
        columns[`${b.uid}-Percentage`] = {
          alignment: TextAlignment.Center,
          displayText: " % ",
          noResize: true,
          testId: b.uid + "-percentage",
        };
        columns[`${b.uid}-Provider-Count`] = {
          alignment: TextAlignment.Center,
          displayText: "Providers",
          noResize: true,
          testId: b.uid + "-provider-count",
        };
      })
    })

    return columns;
  }

  private getColumnGroups = (accuracyTemplate: AccuracyTemplate) => {
    let columnGroups: Array<ColumnGroup<AccuracyAggregateInspectionWafflesToppings>> = [
      {
        children: ["inspection"],
        key: "",
        testId: "inspection",
      },
      {
        children: ["accuracy"],
        key: "",
        testId: "accuracy",
      },
    ];

    const bandColumnGroups: Array<ColumnGroup<AccuracyAggregateInspectionWafflesToppings>> = flatMap(accuracyTemplate.scoring.bandGroups, (bandGroups) => bandGroups.bands.map<ColumnGroup<AccuracyAggregateInspectionWafflesToppings>>((band) => (
      {
        alignment: TextAlignment.Center,
        children: [band.uid + "-Percentage" as keyof AccuracyAggregateInspectionWafflesToppings, band.uid + "-Provider-Count" as keyof AccuracyAggregateInspectionWafflesToppings],
        key: band.name,
        testId: band.uid,
      }
    )));

    columnGroups = columnGroups.concat(bandColumnGroups);

    return columnGroups;
  }

  private getWaffleToppings = (records: FlatAggregateRecord[]) => {
    if (this.state.sortedRecords.length === 0) {
      return [];
    }

    const waffleToppings: AccuracyAggregateInspectionWafflesToppings[] = records.map((sr) => {
      const isInspectionGroup = sr.groupName === sr.name && sr.inspectionGroupUid === sr.inspectionUid;

      const inspection: React.ReactNode = isInspectionGroup ? (
        <div>
          {sr.name}
        </div>
      ) : (
        <div>
          <Link
            key={`name${sr.inspectionUid}`}
            to={{
              pathname: `${this.props.match.url}${this.linkSuffix()}/inspection/${encodeURIComponent(sr.inspectionUid)}`,
              search: location.search,
            }}
          >
            {sr.name}
          </Link>
          {this.state.isSorting &&
            <span
              className="text-muted"
              key={`groupName${sr.inspectionUid}`}
            >
              &nbsp;-&nbsp;
              {sr.groupName}
            </span>
          }
        </div>
      );

      const bands: ProgressBarBand[] = this.getProgressBarBands(sr.bandAggregates);

      const accuracy: React.ReactNode | "N/A" = bands.length > 0 ? (
        <div className="large-pie-chart-container">
          <TooltipWrapper
            text={
              <AccuracyTooltipLegend
                inspectionName={sr.name}
                progressBarBands={bands}
              />
            }
            position="bottom"
          >
            <Pie segments={bands.map((band) => ({ fill: band.color, percent: band.percent / 100 }))} />
          </TooltipWrapper>
        </div>
      ) : "N/A";

      // In the case of Inspection Group UIDs, Waffles uses this key as a CSS selector.
      // Unfortunately, the alphanumeric inspectionGroupUid may start with a digit
      // which is *not* a valid CSS selector. Adding a prefix allows to circumvent this
      const key = isInspectionGroup ? this.getPrefixedKey(sr) : sr.inspectionUid;
      const scoringBands: ScoringBandNode[] = flatMap(this.bandGroups, (bandGroup) => bandGroup.bands.map<ScoringBandNode>((band) => (
        {
          band: band.uid,
          percentage: sr.bandAggregates.find((x) => x.bandUid === band.uid) ? sr.bandAggregates.find((x) => x.bandUid === band.uid).bandPercentage.toFixed(1) : this.resultPossible(band.name, sr.groupName, sr.name) ? "0" : "-",
          providerCount: sr.bandAggregates.find((x) => x.bandUid === band.uid) ? sr.bandAggregates.find((x) => x.bandUid === band.uid).providerCount.toLocaleString() : this.resultPossible(band.name, sr.groupName, sr.name) ? "0" : "-",
        }
      )));

      return new AccuracyAggregateInspectionWafflesToppings(inspection, accuracy, key, scoringBands);
    });

    return waffleToppings;
  }

  private resultPossible = (bandName: string, groupName: string, inspectionName: string): boolean => {
    switch (groupName) {
      case InspectorGroupNames.Completeness:
        return bandName === "Accurate" || bandName === "Inaccurate";
      case InspectorGroupNames.Eligibility:
        return bandName === "Accurate" || bandName === "Inaccurate" || bandName === "Skipped";
      case InspectorGroupNames.Validity:
        return bandName !== "Suspect" || inspectionName === "Address";
      case InspectorGroupNames.Enrichment:
        return bandName === "Enriched" || bandName === "Skipped";
      case InspectorGroupNames.Integrity:
        if (bandName === "Suspect")
          return inspectionName !== "Address Proximity";
        if (bandName === "Inaccurate")
          return inspectionName !== "Excess Specialties";
        return bandName !== "Enriched";
      case InspectorGroupNames.Standardization:
        if (bandName === "Suspect")
          return inspectionName === "Address" || inspectionName === "Standardization";
        return bandName !== "Enriched";
      default:
        return false;
    }
  }

  private linkSuffix = () => {
    let suffix = "";
    if (this.props.regionFilters.state) {
      suffix += `/state/${this.props.regionFilters.state}`;
    }
    if (this.props.regionFilters.county) {
      suffix += `/county/${this.props.regionFilters.county}`;
    }
    if (this.props.regionFilters.specialty) {
      suffix += `/specialty/${this.props.regionFilters.specialty}`;
    }
    return suffix;
  }

  private createSpecialSortElement = () => {
    const band: AccuracyTemplateScoringBand = flatMap(this.bandGroups, (bandGroup) => bandGroup.bands).find((x) => x.bandColor === this.state.selectedSortingColor);

    let tooltipText: JSX.Element;
    if (band) {
      if (this.state.sortingColors.length > 1) {
        tooltipText = (
          <>
            Sorting By: {band.name}
          </>
        );
      } else {
        tooltipText = (
          <>
            All rows are {band.name}
          </>
        );
      }
    }

    return (
      <TooltipWrapper
        text={tooltipText}
        position="bottom"
      >
        <i
          className="fa fa-circle cursor-pointer icon-outline"
          style={{ color: this.state.selectedSortingColor }}
        />
      </TooltipWrapper>
    );
  }

  private setSelectedSortingColor = (column: keyof AccuracyAggregateInspectionWafflesToppings) => {
    // determines the next selected sorting color on circle click
    if (this.state.sortingColors.length > 0) {
      if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
        const index = this.state.sortingColors.indexOf(this.state.selectedSortingColor);

        if (index === this.state.sortingColors.length - 1) {
          // if the currently selected sorting color is the last in the array of possible color options,
          // set the first option in said array as the next selected sorting color
          this.setState({
            selectedSortingColor: this.state.sortingColors[0],
          });
        } else {
          // otherwise, select the next option
          this.setState({
            selectedSortingColor: this.state.sortingColors[index + 1],
          });
        }
      } else {
        // if no color is selected or sorting is occurring in a new column,
        // set the first option in the array of possible color options as the next selected sorting color
        this.setState({
          selectedSortingColor: this.state.sortingColors[0],
        });
      }
    }
  }

  private getSortingColor = (column: keyof AccuracyAggregateInspectionWafflesToppings) => {
    let sortingColor: string = null;
    if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
      // if a selected sorting color exists and the last sorted column is this column,
      // use the selected sorting color stored in the state
      sortingColor = this.state.selectedSortingColor;
    } else {
      // if a sorting color hasn't been selected, or the last column that was sorted is not this column,
      // then select the first option from the color map
      if (this.state.sortingColors.length > 0) {
        sortingColor = this.state.sortingColors[0];

        this.setState({
          selectedSortingColor: sortingColor,
        });
      }
    }

    return sortingColor;
  }

  private customSort = (records: AccuracyAggregateInspectionWafflesToppings[], column: keyof AccuracyAggregateInspectionWafflesToppings, direction: string) => {
    let sortedRecords: FlatAggregateRecord[] = [];
    let isSorting = false;

    if (direction === SortDirections.Asc || direction === SortDirections.Desc) {
      isSorting = true;

      // re-creates a list of distinct provider count and percentage columns based on scoring band uids
      const providerCountBands: string[] = [];
      const percentageBands: string[] = [];
      for (const bandGroup of this.bandGroups) {
        for (const band of bandGroup.bands) {
          const providerCountBand: string = band.uid + "-Provider-Count";
          providerCountBands.push(providerCountBand);

          const percentageBand: string = band.uid + "-Percentage";
          percentageBands.push(percentageBand);
        }
      }

      // retrieves the sorted column's provider count for sorting purposes
      const providerCountSortValue = (bandAggregates: AccuracyBandAggregate[]) => {
        const bandAggregate: AccuracyBandAggregate = bandAggregates.find((x) => x.bandUid + "-Provider-Count" === column);

        if (bandAggregate) {
          return bandAggregate.providerCount;
        }

        return 0;
      };

      // retrieves the sorted column's percentage for sorting purposes
      const percentageSortValue = (bandAggregates: AccuracyBandAggregate[]) => {
        const total: number = bandAggregates.reduce((sum, x) => sum + x.providerCount, 0);
        const bandAggregate: AccuracyBandAggregate = bandAggregates.find((x) => x.bandUid + "-Percentage" === column);

        if (bandAggregate) {
          return total > 0 ? (bandAggregate.providerCount / total) * 100 : 0;
        }

        return 0;
      };

      // retrieves the sorted column's sorting color band percentage
      const accuracyBandPercentage = (bandAggregates: AccuracyBandAggregate[]) => {
        const total: number = bandAggregates.reduce((sum, x) => sum + x.providerCount, 0);
        const sortingColor: string = this.getSortingColor(column);
        const bandAggregate: AccuracyBandAggregate = bandAggregates.find((x) => x.color === sortingColor);

        if (bandAggregate) {
          return total > 0 ? (bandAggregate.providerCount / total) * 100 : 0;
        }

        return 0;
      };

      const multiplier = direction === SortDirections.Asc ? 1 : -1;

      if (providerCountBands.includes(column)) {
        sortedRecords = this.state.sortedRecords.sort((a, b) => (providerCountSortValue(a.bandAggregates) > providerCountSortValue(b.bandAggregates) ? 1 : -1) * multiplier);
      } else if (percentageBands.includes(column)) {
        sortedRecords = this.state.sortedRecords.sort((a, b) => (percentageSortValue(a.bandAggregates) > percentageSortValue(b.bandAggregates) ? 1 : -1) * multiplier);
      } else {
        switch (column) {
          case "inspection":
            sortedRecords = this.state.sortedRecords.sort((a, b) => (a.name + a.groupName > b.name + b.groupName ? 1 : -1) * multiplier);
            break;
          case "accuracy":
            sortedRecords = this.state.sortedRecords.sort((a, b) => (accuracyBandPercentage(a.bandAggregates) > accuracyBandPercentage(b.bandAggregates) ? 1 : -1) * multiplier);
            break;
          default:
            console.warn(`No sort definition for column ${column}.`);
        }
      }
    } else {
      // in order to avoid modifying the original records' sort order next time a sort occurs, clone them
      sortedRecords = JSON.parse(JSON.stringify(this.state.records));
    }

    this.setState({
      isSorting,
      sortedColumn: column,
      sortedRecords,
    });

    return records;
  }

  private setSelectedRecords = (recordKeys: string[]) => {
    this.setState({
      selectedRecords: recordKeys,
    });
  }

  private getProgressBarBands = (bandAggregates: AccuracyBandAggregate[]): ProgressBarBand[] => {
    const total: number = bandAggregates.reduce((sum, x) => sum + x.providerCount, 0);

    return bandAggregates.map<ProgressBarBand>((a) => {
      const percent = total > 0 ? (a.providerCount / total) * 100 : 0;
      return {
        color: a.color,
        colorType: "hex",
        justification: a.justification,
        outOfScale: percent > 0 && percent < 0.1,
        percent: this.getBandPercent(percent),
        tooltip: `${a.name ? a.name + ": " : ""}${Intl.NumberFormat("en-US").format(a.providerCount)} providers`,
      };
    });
  }

  /**
   * Enforces smallest increment is rendered if percent value is smaller than 0.1
   * @param percent percent value in question
   */
  private getBandPercent = (percent: number) => {
    return percent
      ? (percent > 0 && percent < 0.1) ? 0.3 : percent // 0.3 is the smallest visible segment
      : 0;
  }

  private getPrefixedKey = (record: FlatAggregateRecord): string => `${record.groupName}-${record.inspectionUid}`;
}

const WrappedAccuracyAggregateInspectionDetails = withCountyProjectContext(AccuracyAggregateInspectionDetails);

export default WrappedAccuracyAggregateInspectionDetails;
